import { ProductDecorationMethod } from '@codezo/graphql';
import { Plus, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NumberInput } from '@/components/ui/number-input';
import { SelectPopover } from '@/components/ui/select-popover';
import { UnitToggle } from './SpecValueInput';
import { DECORATION_METHODS, DECORATION_METHOD_LABELS } from '@codezo/product-specs';
import { makeSpecDecorationRow, type SpecDecorationRow } from '@codezo/product-specs';

const METHOD_OPTIONS = DECORATION_METHODS.map((method) => ({
	value: method,
	label: DECORATION_METHOD_LABELS[method],
}));

const AREA_UNITS = ['cm', 'mm', 'in'] as const;

/**
 * Print and decoration entries. A product can carry several — a screen print
 * on the front and an engraving on the base are separate rows.
 */
export function SpecDecorationsEditor({
	value,
	onChange,
	disabled,
}: {
	value: SpecDecorationRow[];
	onChange: (next: SpecDecorationRow[]) => void;
	disabled?: boolean;
}) {
	const patch = (uid: string, partial: Partial<SpecDecorationRow>) =>
		onChange(value.map((row) => (row.uid === uid ? { ...row, ...partial } : row)));

	return (
		<div className="flex flex-col gap-2">
			<div className="flex items-center justify-between">
				<Label className="text-xs uppercase tracking-[0.06em] text-text-4">
					Print &amp; decoration
				</Label>
				<button
					type="button"
					disabled={disabled}
					onClick={() => onChange([...value, makeSpecDecorationRow()])}
					className="flex cursor-pointer items-center gap-1 rounded-md px-1.5 py-1 text-xs text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1 disabled:cursor-default disabled:opacity-50"
				>
					<Plus size={12} />
					Add decoration
				</button>
			</div>

			{value.length === 0 ? (
				<p className="text-xs text-text-4">No decorations specified.</p>
			) : (
				value.map((row) => (
					<div
						key={row.uid}
						className="flex flex-col gap-2 rounded-lg border border-line-1 bg-surface p-2.5"
					>
						<div className="flex items-center gap-2">
							<div className="min-w-0 flex-1">
								<SelectPopover
									value={row.method}
									onChange={(method) =>
										patch(row.uid, {
											method: method as ProductDecorationMethod,
										})
									}
									options={METHOD_OPTIONS}
									placeholder="Select a method"
								/>
							</div>
							<button
								type="button"
								disabled={disabled}
								onClick={() => onChange(value.filter((d) => d.uid !== row.uid))}
								className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-text-4 transition-colors hover:bg-surface-hover hover:text-destructive disabled:cursor-default disabled:opacity-50"
								aria-label="Remove decoration"
							>
								<Trash2 size={14} />
							</button>
						</div>

						{/* The enum has no name for OTHER, so the note carries it. */}
						{row.method === ProductDecorationMethod.Other && (
							<Input
								value={row.methodNote}
								onChange={(e) => patch(row.uid, { methodNote: e.target.value })}
								placeholder="Name the method"
								maxLength={80}
								disabled={disabled}
							/>
						)}

						<div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
							<Input
								value={row.placement}
								onChange={(e) => patch(row.uid, { placement: e.target.value })}
								placeholder="Placement, e.g. Front centre"
								maxLength={80}
								disabled={disabled}
							/>
							<div className="flex items-center gap-2">
								<span className="shrink-0 text-xs text-text-4">Colours</span>
								<NumberInput
									value={row.colorCount}
									onChange={(e) =>
										patch(row.uid, {
											colorCount: (e.target as HTMLInputElement).value,
										})
									}
									placeholder="0"
									min={0}
									step={1}
									disabled={disabled}
								/>
							</div>
						</div>

						<div className="flex flex-wrap items-center gap-2">
							<span className="shrink-0 text-xs text-text-4">Print area</span>
							<NumberInput
								value={row.areaWidth}
								onChange={(e) =>
									patch(row.uid, {
										areaWidth: (e.target as HTMLInputElement).value,
									})
								}
								placeholder="W"
								min={0}
								step={0.1}
								disabled={disabled}
								className="w-[105px]"
							/>
							<span className="text-xs text-text-4">×</span>
							<NumberInput
								value={row.areaHeight}
								onChange={(e) =>
									patch(row.uid, {
										areaHeight: (e.target as HTMLInputElement).value,
									})
								}
								placeholder="H"
								min={0}
								step={0.1}
								disabled={disabled}
								className="w-[105px]"
							/>
							<UnitToggle
								units={AREA_UNITS}
								value={row.areaUnit}
								onChange={(areaUnit) => patch(row.uid, { areaUnit })}
								disabled={disabled}
							/>
						</div>

						<Input
							value={row.notes}
							onChange={(e) => patch(row.uid, { notes: e.target.value })}
							placeholder="Notes (optional)"
							maxLength={200}
							disabled={disabled}
						/>
					</div>
				))
			)}
		</div>
	);
}
